import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  iconRetinaUrl: iconRetina,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

interface LocationPickerProps {
  latitude: number;
  longitude: number;
  onLocationChange: (lat: number, lng: number) => void;
  height?: string;
  zoom?: number;
}

// Component to handle map clicks
const MapClickHandler: React.FC<{ onLocationChange: (lat: number, lng: number) => void }> = ({ onLocationChange }) => {
  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;
      onLocationChange(lat, lng);
    },
  });
  return null;
};

const LocationPicker: React.FC<LocationPickerProps> = ({
  latitude,
  longitude,
  onLocationChange,
  height = '400px',
  zoom = 13
}) => {
  const [position, setPosition] = useState<[number, number]>([latitude, longitude]);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (latitude && longitude) {
      const newPosition: [number, number] = [latitude, longitude];
      setPosition(newPosition);
      // Center map on new position
      if (mapRef.current) {
        mapRef.current.setView(newPosition, zoom);
      }
    }
  }, [latitude, longitude, zoom]);

  const handleLocationChange = (lat: number, lng: number) => {
    setPosition([lat, lng]);
    onLocationChange(lat, lng);
  };

  // Default to Gaborone, Botswana if no coordinates provided
  const defaultPosition: [number, number] = latitude && longitude
    ? [latitude, longitude]
    : [-24.6282, 25.9231]; // Gaborone coordinates

  return (
    <div className="w-full rounded-lg overflow-hidden border border-gray-300" style={{ height }}>
      <MapContainer
        center={defaultPosition}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {position[0] && position[1] && (
          <Marker position={position} />
        )}
        <MapClickHandler onLocationChange={handleLocationChange} />
      </MapContainer>
      <div className="bg-white p-2 border-t border-gray-300 text-xs text-gray-600">
        <p>📍 Click on the map to select location</p>
        {position[0] && position[1] && (
          <p className="mt-1">Coordinates: {position[0].toFixed(6)}, {position[1].toFixed(6)}</p>
        )}
      </div>
    </div>
  );
};

export default LocationPicker;



